import Menu from '@/global/components/MenuV2'
import { useAuth } from '@/hooks'
import { hasPermission } from '@/hooks/useBackendMenu'
import { CategoriaEnum } from '@acme/auth'
import { Grid } from '@mui/material'
import dayjs from 'dayjs'
import { FormField, FormFile } from 'global/components/Form'
import { useEffect, useMemo } from 'react'
import { useFormContext } from 'react-hook-form'
import {
    AfastamentoV2,
    ocorrencia,
    ocorrenciaTipoToPermission,
} from 'service/api'
import TabFuncionariosForm from '../atoms/TabsFuncionarios'
import { funcionarioPodeSolicitarAjustes } from '../modals/DetalhesRegistros'

interface IProps {
    funcionarioId?: number
    setIds?: (ids: number[]) => void
}

export default function FormNovoAfastamento({ funcionarioId, setIds }: IProps) {
    const { data: ocorrencias = [] } = ocorrencia.useFetch({
        refetchOnWindowFocus: false,
    })
    const { watch, setValue } = useFormContext<
        AfastamentoV2 & { dias: number }
    >()
    const {
        decoded: { categoria },
    } = useAuth()
    const [dataInicial, dataFinal, dias] = watch([
        'dataInicial',
        'dataFinal',
        'dias',
    ])

    const isDiasFocado = document.activeElement?.id === 'field-dias'
    const isDataFocado = ['field-data-final', 'field-data-inicial'].includes(
        document.activeElement?.id ?? '',
    )

    useEffect(() => {
        if (isDiasFocado) {
            if (!dataInicial || dias < 1) return
            const dataFinal = dayjs(dataInicial)
                .add(dias - 1, 'day')
                .format('YYYY-MM-DD')
            setValue('dataFinal', dataFinal as any)
        }
        if (isDataFocado) {
            if (!dataInicial || dayjs(dataFinal).isBefore(dataInicial)) return
            setValue('dias', dayjs(dataFinal).diff(dataInicial, 'day') + 1)
        }
    }, [dataInicial, dataFinal, isDiasFocado, isDataFocado, setValue, dias])

    const funcionarioPodeAjustar = funcionarioPodeSolicitarAjustes()

    const MemoizedFuncionarios = useMemo(
        () => (
            <TabFuncionariosForm
                funcionarioIds={funcionarioId ? [funcionarioId] : []}
                onChange={
                    setIds
                        ? setIds
                        : () => {
                              void 0
                          }
                }
            />
        ),
        [funcionarioId, setIds],
    )

    return (
        <Grid container spacing={2} rowGap={2} alignItems="center">
            {MemoizedFuncionarios}
            <Grid item xs={12} sx={{ mt: -3 }}>
                <FormField
                    name="ocorrenciaId"
                    label="Ocorrência"
                    select
                    required
                >
                    {ocorrencias
                        .filter(
                            it =>
                                funcionarioPodeAjustar ||
                                hasPermission([
                                    'create',
                                    ocorrenciaTipoToPermission(it.tipo),
                                ]),
                        )
                        .filter(
                            categoria === CategoriaEnum.Supervisor
                                ? it => !it.disabled
                                : () => true,
                        )
                        .map(({ codigo, id, descricao, tipo }, index) => (
                            <Menu.Item
                                key={index}
                                value={id}
                                disabled={
                                    !funcionarioPodeAjustar &&
                                    !hasPermission([
                                        'create',
                                        ocorrenciaTipoToPermission(tipo),
                                    ])
                                }
                            >
                                {`${String(codigo).padStart(
                                    3,
                                    '0',
                                )}: ${descricao}`}
                            </Menu.Item>
                        ))}
                </FormField>
            </Grid>
            <Grid item xs={5} sx={{ mt: -3 }}>
                <FormField
                    id="field-data-inicial"
                    name="dataInicial"
                    label="Data inicial"
                    type="date"
                    required
                    InputLabelProps={{
                        shrink: true,
                    }}
                />
            </Grid>
            <Grid item xs={5} sx={{ mt: -3 }}>
                <FormField
                    required
                    id="field-data-final"
                    name="dataFinal"
                    label="Data final"
                    type="date"
                    InputLabelProps={{
                        shrink: true,
                    }}
                />
            </Grid>
            <Grid item xs={2} sx={{ mt: -3 }}>
                <FormField
                    id="field-dias"
                    name="dias"
                    label="Dias"
                    type="number"
                    disabled={!dataInicial}
                    InputLabelProps={{
                        shrink: true,
                    }}
                />
            </Grid>
            <Grid item xs={12} sx={{ mt: -3 }}>
                <FormField
                    name="justificativa"
                    label="Justificativa"
                    multiline
                    max={190}
                />
            </Grid>
            <Grid item xs={12} sx={{ mt: -3 }}>
                <FormFile name="fileUrl" />
            </Grid>
        </Grid>
    )
}
